import React, { useCallback, useEffect, useRef, useState } from "react";
import { Camera, RotateCcw, Check } from "lucide-react";
import { Button } from "../ui/button";

export function CameraCapture({ label, onCapture, existingPreviewUrl }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState("");
  const [previewUrl, setPreviewUrl] = useState(existingPreviewUrl || null);
  const [active, setActive] = useState(!existingPreviewUrl);

  const startCamera = useCallback(async () => {
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 720 }, height: { ideal: 960 } }, audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setActive(true);
    } catch (e) {
      setError("Could not access the camera. Check camera permissions and try again.");
    }
  }, []);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    if (active) startCamera();
    return () => stopCamera();
  }, [active, startCamera, stopCamera]);

  const capture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setActive(false);
      stopCamera();
      onCapture(blob, url);
    }, "image/jpeg", 0.85);
  };

  const retake = () => {
    setPreviewUrl(null);
    setActive(true);
  };

  return (
    <div className="rounded-md border border-border bg-surface p-3" data-testid={`camera-capture-${label}`}>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">{label}</p>
      {error && <p className="mb-2 text-xs text-danger" data-testid={`camera-capture-error-${label}`}>{error}</p>}
      {active ? (
        <div className="space-y-2">
          <video ref={videoRef} autoPlay playsInline muted className="aspect-[3/4] w-full rounded-md bg-black object-cover" />
          <Button type="button" className="w-full" onClick={capture} data-testid={`camera-capture-shutter-${label}`}>
            <Camera size={16} strokeWidth={1.5} /> Capture Photo
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {previewUrl && (
            <img src={previewUrl} alt={label} className="aspect-[3/4] w-full rounded-md object-cover" data-testid={`camera-capture-preview-${label}`} />
          )}
          <Button type="button" variant="outline" className="w-full" onClick={retake} data-testid={`camera-capture-retake-${label}`}>
            <RotateCcw size={16} strokeWidth={1.5} /> Retake
          </Button>
        </div>
      )}
    </div>
  );
}

export function CapturedBadge() {
  return <Check size={16} strokeWidth={2} className="text-success" />;
}
